import {useNavigate} from 'react-router-dom'
import {zodResolver} from '@hookform/resolvers/zod'
import {LoaderCircle} from 'lucide-react'
import {useForm} from 'react-hook-form'
import {z} from 'zod'
import {Button, Input, Label} from '@/shared/ui'
import {useAuthStore} from '../model/authStore'

const registerSchema = z
  .object({
    email: z.email('Некорректный email'),
    password: z.string().min(4, 'Минимум 4 символа'),
    confirmPassword: z.string().min(1, 'Повторите пароль'),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'Пароли не совпадают',
    path: ['confirmPassword'],
  })

type RegisterFormValues = z.infer<typeof registerSchema>

export function RegisterForm() {
  const navigate = useNavigate()
  const register = useAuthStore(s => s.register)
  const isLoading = useAuthStore(s => s.isLoading)

  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {email: '', password: '', confirmPassword: ''},
  })

  const onSubmit = form.handleSubmit(async data => {
    try {
      await register(data.email, data.password)
      navigate('/verify-email', {state: {email: data.email}})
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Произошла ошибка. Попробуйте снова.'
      form.setError('root', {message})
    }
  })

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      <div className="space-y-2">
        <Label htmlFor="register-email">Email</Label>
        <Input
          id="register-email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          aria-invalid={Boolean(form.formState.errors.email)}
          {...form.register('email')}
        />
        {form.formState.errors.email && (
          <p className="text-sm text-destructive">{form.formState.errors.email.message}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="register-password">Пароль</Label>
        <Input
          id="register-password"
          type="password"
          autoComplete="new-password"
          placeholder="••••••••"
          aria-invalid={Boolean(form.formState.errors.password)}
          {...form.register('password')}
        />
        {form.formState.errors.password && (
          <p className="text-sm text-destructive">{form.formState.errors.password.message}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="register-confirm">Повторите пароль</Label>
        <Input
          id="register-confirm"
          type="password"
          autoComplete="new-password"
          placeholder="••••••••"
          aria-invalid={Boolean(form.formState.errors.confirmPassword)}
          {...form.register('confirmPassword')}
        />
        {form.formState.errors.confirmPassword && (
          <p className="text-sm text-destructive">
            {form.formState.errors.confirmPassword.message}
          </p>
        )}
      </div>
      {form.formState.errors.root && (
        <p className="text-sm text-destructive">{form.formState.errors.root.message}</p>
      )}
      <Button type="submit" size="lg" className="w-full" disabled={isLoading}>
        {isLoading ? (
          <>
            <LoaderCircle className="size-5 animate-spin" />
            Создание...
          </>
        ) : (
          'Создать аккаунт'
        )}
      </Button>
      <p className="text-center text-sm text-muted-foreground">
        Уже есть аккаунт?{' '}
        <button
          type="button"
          className="cursor-pointer text-primary underline-offset-4 hover:underline"
          onClick={() => navigate('/login')}
        >
          Войти
        </button>
      </p>
    </form>
  )
}
